import axios from "axios";
import { navigate } from "../hooks/useNavigateHook.js";
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
} from "../utils/browserStorage.js";
import {
  GOOGLE_LOGIN,
  REFRESH_ACCESS_TOKEN,
  USER_LOGIN,
} from "../utils/apiConstants.js";
import paths from "../utils/paths.js";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const REFRESH_ENDPOINT = REFRESH_ACCESS_TOKEN;
const AUTH_REFRESH_EXCLUDED_ENDPOINTS = [
  USER_LOGIN,
  GOOGLE_LOGIN,
  REFRESH_ENDPOINT,
];

const api = axios.create({
  baseURL: BASE_URL,
});

let isRefreshing = false;
let failedQueue = [];

function enqueueRequest(requestConfig) {
  return new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject, requestConfig });
  });
}

function processQueue(error, newToken = null) {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      if (newToken) {
        prom.requestConfig.headers["Authorization"] = `Bearer ${newToken}`;
      }
      prom.resolve(api(prom.requestConfig));
    }
  });
  failedQueue = [];
}

function shouldSkipAuthRefresh(config = {}) {
  if (config.skipAuthRefresh) return true;

  const requestUrl = config.url || "";
  return AUTH_REFRESH_EXCLUDED_ENDPOINTS.some((endpoint) =>
    requestUrl.endsWith(endpoint)
  );
}

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    } else if (!config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/json";
    }
    return config;
  },
  (err) => Promise.reject(err)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const { config, response } = error;
    const originalRequest = { ...config };

    if (!response) {
      return Promise.reject(error);
    }

    if (response.status !== 401 || shouldSkipAuthRefresh(config)) {
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      clearTokens();
      navigate(paths.Root);
      return Promise.reject(error);
    }
    originalRequest._retry = true;

    if (isRefreshing) {
      return enqueueRequest(originalRequest);
    }

    isRefreshing = true;
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      isRefreshing = false;
      processQueue(error, null);
      clearTokens();
      navigate(paths.Root);
      return Promise.reject(error);
    }

    return axios
      .post(
        BASE_URL + REFRESH_ENDPOINT,
        { refreshToken },
        {
          headers: { "Content-Type": "application/json" },
        }
      )
      .then(({ data }) => {
        const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
          data.data;
        setTokens({
          accessToken: newAccessToken,
          refreshToken: newRefreshToken,
        });

        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;

        processQueue(null, newAccessToken);

        return api(originalRequest);
      })
      .catch((err) => {
        processQueue(err, null);
        clearTokens();
        navigate(paths.Root);
        return Promise.reject(err);
      })
      .finally(() => {
        isRefreshing = false;
      });
  }
);

export default api;
